import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	AuthnService,
	CompanyService,
	DeploymentContext,
	ServiceBase,
} from '@Common';
import {
	AggregateAssetResult,
	Asset,
	AssetSearchResult,
	Filter,
	urlIdentifierForCompany,
} from 'company-finder-common';

@Injectable({ providedIn: 'root' })
export class AssetService extends ServiceBase {
	public currentSearchResults: Asset[] = null;
	private searchUrl: string;
	private aggregateSearchUrl: string;

	constructor(
		httpClient: HttpClient,
		dc: DeploymentContext,
		private companyService: CompanyService,
		private authnService: AuthnService
	) {
		super(httpClient, dc, '/asset');
		this.searchUrl = dc.buildApiUrl('/search/asset');
		this.aggregateSearchUrl = dc.buildApiUrl('/search/asset/aggregate');
	}

	public async getByName(name: string): Promise<Asset> {
		return this.get(`${this._apiUrl}/${urlIdentifierForCompany(name)}`);
	}

	public async searchAndFilterAggregate(
		filter: Filter
	): Promise<AggregateAssetResult> {
		const filterParam = this.companyService
			.reduceFilter(filter, true)
			.toString();

		return this.get(
			`${this.aggregateSearchUrl}?filter=${encodeURIComponent(
				filterParam
			)}&${this.getCacheableParams(filter?.isUserSpecific)}`,
			{ skipURIEncoding: true }
		);
	}

	public async searchAndFilter(
		predicate: string,
		from: number = 0,
		size: number = 0,
		filter: Filter = Filter.emptyFilter
	): Promise<AssetSearchResult> {
		const filterParam = `filter=${encodeURIComponent(
			this.companyService.reduceFilter(filter, true).toString()
		)}`;

		// At one point we excluded "default" sort configurations, but making them explicit helps with debugging and consistency
		const sortTypeParam = filter?.sort?.type
			? `&sortType=${encodeURIComponent(filter.sort.type)}`
			: '';
		const sortDirectionParam = filter?.sort?.direction
			? `&sortDirection=${encodeURIComponent(filter.sort.direction)}`
			: '';
		const sortFieldParam = filter?.sort?.field
			? `&sortField=${encodeURIComponent(filter.sort.field)}`
			: '';

		const predicateParam =
			predicate !== ''
				? `&predicate=${encodeURIComponent(predicate)}`
				: ``;
		const cacheableParams = this.getCacheableParams(filter?.isUserSpecific);

		const fromParam = from > 0 ? `&from=${from}` : '';
		const sizeParam = size !== 20 ? `&size=${size}` : '';

		return this.get<AssetSearchResult>(
			`${this.searchUrl}?${filterParam}${predicateParam}${sortTypeParam}${sortDirectionParam}${sortFieldParam}${fromParam}${sizeParam}&${cacheableParams}`,
			{ skipURIEncoding: true }
		);
	}

	private getCacheableParams(includeUser: boolean): string {
		return includeUser
			? `${this._context.cacheableParams}&user=${this.authnService.userId}`
			: this._context.cacheableParams;
	}
}
